import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import mq from '../../utils/mq';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import HeroSlide from './HeroSlide';

import HeroCarouselTextContent from './HeroCarouselTextContent';
import StatItem from '../logo-marquee-with-title-and-stats/StatItem';

interface StatsContainerOuterProps {
  bottomOffset: number;
}

const HeroCarouselOuterContainer = styled.div`
  width: 100%;
  height: 100svh;
  max-height: 800px;
  min-height: fit-content;
  overflow-y: visible;
  position: relative;
  z-index: 3;
  ${mq.mobile(css`
    max-height: 700px;
  `)};
`;

const Container = styled.div`
  width: 100%;
  max-width: calc(1240px + 80px);
  margin-left: auto;
  margin-right: auto;
  padding-left: 40px;
  padding-right: 40px;

  ${mq.mobile(css`
    padding-left: 20px;
    padding-right: 20px;
  `)};
`;

const HeroCarouselInnerContainer = styled.div`
  width: 100%;
  height: 100%;

  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const EmblaContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const StatsContainerOuter = styled.div<StatsContainerOuterProps>`
  position: absolute;
  bottom: ${({ bottomOffset }) => `${bottomOffset}px`};
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  @media (max-width: 976px) {
    display: none;
  }
`;

const MobileStatsContainer = styled.div`
  display: none;
  @media (max-width: 976px) {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding-top: 32px;
    background: var(--Color-Grey-05, #f8f8f8);
  }
`;

const StatsContainer = styled.div`
  padding: 24px 46px;
  border-radius: var(--Radius-Radius-3, 16px);
  background: var(--Background-bg-primary, #fff);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: start;
`;

export default function HeroCarousel({
  slides,
  loopSlides = true,
  enableAutoplay = true,
  autoplayDelay = 10000,
  enableSwipeGestures = false,
  statItems
}: {
  slides: any[];
  loopSlides?: boolean;
  enableAutoplay?: boolean;
  autoplayDelay?: number;
  enableSwipeGestures?: boolean;
  statItems?: StatsObjectProps['statItems'];
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: loopSlides, watchDrag: enableSwipeGestures },
    [enableAutoplay && (Autoplay({ delay: autoplayDelay }) as any)]
  );

  const [activeSlide, setActiveSlide] = useState(0);

  const nextSlide = () => {
    if (emblaApi) {
      emblaApi.scrollNext();
    }
  };

  const previousSlide = () => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  };

  emblaApi?.on('scroll', () => {
    setActiveSlide(emblaApi?.selectedScrollSnap() || 0);
    emblaApi.plugins().autoplay.reset();
  });

  return (
    <>
      <HeroCarouselOuterContainer>
        <Container style={{ zIndex: 3, position: 'relative', height: '100%' }}>
          <HeroCarouselTextContent
            slides={slides}
            activeIndex={activeSlide}
            onPrev={previousSlide}
            onNext={nextSlide}
          />
        </Container>
        <HeroCarouselInnerContainer ref={emblaRef as any} className="embla">
          <EmblaContainer className="embla__container">
            {slides.map((slide, index) => (
              <HeroSlide
                key={index}
                {...slide}
                nextSlide={nextSlide}
                previousSlide={previousSlide}
              />
            ))}
          </EmblaContainer>
          {statItems && statItems?.length > 0 && (
            <StatsObject statItems={statItems} />
          )}
        </HeroCarouselInnerContainer>
      </HeroCarouselOuterContainer>
      {statItems && statItems?.length > 0 && (
        <MobileStatsObject statItems={statItems} />
      )}
    </>
  );
}

interface StatItemContent {
  id: string | number;
  valuePrefix: string;
  value: string;
  valueSuffix: string;
  subtitle: string;
  startValue?: number;
  durationMs?: number;
  easingFn?: (t: number) => number;
  visibilityThreshold?: number;
  formatNumber?: (n: number) => string;
  triggerOnce?: boolean;
  enableAnimation?: boolean;
  smallVariant?: boolean;
  verySmallVariant?: boolean;
}

interface StatsObjectProps {
  statItems: ReadonlyArray<StatItemContent>;
}

const StatsObject = ({ statItems }: StatsObjectProps) => {
  const statsContainerRef = useRef<HTMLDivElement | null>(null);
  const [bottomOffset, setBottomOffset] = useState(-73);

  useEffect(() => {
    const handleResize = () => {
      if (!statsContainerRef.current) return;
      const height = statsContainerRef.current.offsetHeight;
      setBottomOffset(-height / 2);
    };

    handleResize();

    let resizeObserver: ResizeObserver | undefined;

    if (statsContainerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(statsContainerRef.current);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      resizeObserver?.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return (
    <StatsContainerOuter bottomOffset={bottomOffset}>
      <StatsContainer ref={statsContainerRef}>
        {statItems.map((stat) => (
          <StatItem key={stat.id} {...stat} verySmallVariant />
        ))}
      </StatsContainer>
    </StatsContainerOuter>
  );
};

const MobileStatsObject = ({ statItems }: StatsObjectProps) => {
  return (
    <MobileStatsContainer>
      {statItems.map((stat) => (
        <StatItem key={stat.id} {...stat} verySmallVariant />
      ))}
    </MobileStatsContainer>
  );
};
